import { SelectionChangedEvent } from 'ag-grid-community';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ComposedTable } from 'components';
import { CustomLoadingOverlay } from 'components/CustomLoadingOverlay';
import {
  setCancelBtnDisabled,
  getPrintChecksResponse,
  setSelectedVendors
} from 'pages/VendorsTablePage/components/PrintChecks';
import { labels } from 'pages/VendorsTablePage/components/PrintChecks/messages';
import { PrintChecksFooter } from 'pages/VendorsTablePage/components/PrintChecks/PrintChecksFooter';
import { PrintChecksVendorData } from 'pages/VendorsTablePage/components/PrintChecks/types';
import { usePrintChecksTableColDefs } from 'pages/VendorsTablePage/components/PrintChecks/usePrintChecksTableColDefs';
import { DEFAULT_COL_WIDTH } from 'pages/VendorsTablePage/components/VendorsTable/constants';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  HIGH_VOLUME_BLOCK_LOAD_DEBOUNCE,
  GRID_INITIAL_ROW_COUNT,
  HIGH_VOLUME_PRINT_CHECKS_PAGE_SIZE,
  HIGH_VOLUME_MAX_PAGES_IN_CACHE,
  HIGH_VOLUME_MIN_SEARCH_LENGTH
} from '../constants';
import { getServerSideSelectedRows } from '../utils';
import {
  StyledHighVolumeGridContainer,
  StyledHighVolumeGridDimension,
  StyledHighVolumeGridElement
} from './styled';
import { usePrintChecksServerSideDatasource } from './usePrintChecksServerSideDatasource';
import { useSelectAllPrintChecks } from './useSelectAllPrintChecks';

export const HighVolumePrintChecksTable = (): React.JSX.Element => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const data = useAppSelector(getPrintChecksResponse);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [isCheckSelected, setIsCheckSelected] = useState<boolean>(false);
  const [debouncedSearchInputValue, setDebouncedSearchInputValue] = useState<
    string | undefined
  >(undefined);

  const {
    isFilterChanged,
    isLoading,
    serverSideDatasource,
    gridRowData,
    columnState
  } = usePrintChecksServerSideDatasource({
    searchValue: debouncedSearchInputValue
  });

  const { refetch } = useSelectAllPrintChecks({
    searchValue: debouncedSearchInputValue,
    isSelectAll: isCheckSelected,
    columnState
  });

  const onSearchInputChange = (newValue: string) =>
    setSearchInputValue(newValue);

  const onDebouncedSearchInputChange = (searchValue: string) =>
    setDebouncedSearchInputValue(
      searchValue?.length >= HIGH_VOLUME_MIN_SEARCH_LENGTH
        ? searchValue
        : undefined
    );

  useEffect(() => {
    dispatch(setCancelBtnDisabled(isLoading));
  }, [dispatch, isLoading]);

  const handleRowSelection = ({ api }: SelectionChangedEvent): void => {
    const gridSelectedRows = api.getSelectedRows();
    const selectedRowsIds = api?.getServerSideSelectionState()?.toggledNodes;
    if (gridSelectedRows?.length === gridRowData?.length) {
      setIsCheckSelected(true);
      refetch();
    } else {
      const selectedRows = getServerSideSelectedRows(
        gridRowData,
        selectedRowsIds
      );
      dispatch(setSelectedVendors(selectedRows));
    }
  };

  const columnDefs = usePrintChecksTableColDefs(isLoading && isFilterChanged);

  return (
    <>
      <StyledHighVolumeGridContainer>
        <StyledHighVolumeGridElement>
          <StyledHighVolumeGridDimension>
            <ComposedTable<PrintChecksVendorData>
              columnDefs={columnDefs}
              defaultColDef={{
                minWidth: DEFAULT_COL_WIDTH
              }}
              getRowId={param => param.data.vendorNo}
              isSearchable
              isDownloadable={false}
              isPrintable={false}
              loadingOverlayComponent={CustomLoadingOverlay}
              rowModelType="serverSide"
              groupSelectsChildren
              serverSideDatasource={serverSideDatasource}
              onSelectionChanged={handleRowSelection}
              cacheBlockSize={HIGH_VOLUME_PRINT_CHECKS_PAGE_SIZE}
              maxBlocksInCache={HIGH_VOLUME_MAX_PAGES_IN_CACHE}
              blockLoadDebounceMillis={HIGH_VOLUME_BLOCK_LOAD_DEBOUNCE}
              purgeClosedRowNodes={false}
              reactiveCustomComponents
              searchInputValue={searchInputValue}
              onSearchInputChange={onSearchInputChange}
              onDebouncedSearchInputChange={onDebouncedSearchInputChange}
              serverSidePivotResultFieldSeparator="_"
              serverSideOnlyRefreshFilteredGroups={false}
              suppressMultiSort
              serverSideInitialRowCount={GRID_INITIAL_ROW_COUNT}
              scrollToTopButtonShow={false}
              suppressServerSideFullWidthLoadingRow={isFilterChanged}
              suppressRowClickSelection
              suppressRowDeselection
              tableHeaderCount={data?.totalNoOfVendors}
              tableHeaderTitle={intl.formatMessage(labels.printChecksTable)}
              useCustomSearchFilter={false}
            />
          </StyledHighVolumeGridDimension>
        </StyledHighVolumeGridElement>
      </StyledHighVolumeGridContainer>
      <PrintChecksFooter data={data ?? undefined} />
    </>
  );
};
